import React, { lazy, Suspense } from 'react';
import ProtectedRoute from '../../shared/components/ProtectedRoute';
import LoadingSpinner from '../../shared/components/LoadingSpinner';
import ErrorPage from '../../error-page';

// Lazy load payables components
const PayableNavigation = lazy(() => import('../../routes/payables/payable-navigation'));
const SchedulePayableSummaries = lazy(() => import('../../routes/payables/schedule-payables'));
const BeefSlaughterController = lazy(() => import('../../routes/payables/beef-slaughter-controller'));
const PorkSlaughterController = lazy(() => import('../../routes/payables/pork-slaughter-controller'));
const ProducerPayableHistory = lazy(() => import('../../routes/payables/producer-payable-history'));
const HaulerPayableHistory = lazy(() => import('../../routes/payables/hauler-payable-history'));
const CouncilPayables = lazy(() => import('../../routes/payables/council-payables'));
const LivestockCenterPayables = lazy(() => import('../../routes/payables/livestock-center-payables'));
const PorkSlaughterSummaryController = lazy(() => import('../../routes/payables/pork-slaughter-summary-controller'));
const HaulerPayableController = lazy(() => import('../../routes/payables/hauler-payable-controller'));
const ProducerPayableController = lazy(() => import('../../routes/payables/producer-payable-controller'));
const BeefProducerPayableDetail = lazy(() => import('../../routes/payables/beef-producer-payable-detail'));
const PorkProducerPayableDetail = lazy(() => import('../../routes/payables/pork-producer-payable-detail'));
const PostAdjustmentList = lazy(() => import('../../routes/payables/post-adjustment-list'));

/**
 * Payables feature routes with lazy loading
 */
export const payablesRoutes = [
  {
    path: '/payables/:poolCode/:livestockTypeId',
    element: (
      <ProtectedRoute requiredRoles={['LMS.Payables']}>
        <Suspense fallback={<LoadingSpinner message="Loading payables..." />}>
          <PayableNavigation />
        </Suspense>
      </ProtectedRoute>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/payables/beef-slaughter/:scheduleId/:poolCode/:livestockTypeId',
    element: (
      <ProtectedRoute requiredRoles={['LMS.Payables']}>
        <Suspense fallback={<LoadingSpinner message="Loading beef slaughter data..." />}>
          <BeefSlaughterController />
        </Suspense>
      </ProtectedRoute>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/payables/pork-slaughter/:scheduleId/:poolCode/:livestockTypeId',
    element: (
      <ProtectedRoute requiredRoles={['LMS.Payables']}>
        <Suspense fallback={<LoadingSpinner message="Loading pork slaughter data..." />}>
          <PorkSlaughterController />
        </Suspense>
      </ProtectedRoute>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/payables/pork-slaughter-summary/:scheduleId/:poolCode/:livestockTypeId',
    element: (
      <ProtectedRoute requiredRoles={['LMS.Payables']}>
        <Suspense fallback={<LoadingSpinner message="Loading summary..." />}>
          <PorkSlaughterSummaryController />
        </Suspense>
      </ProtectedRoute>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/payables/hauler-payable/:scheduleId/:poolCode/:livestockTypeId',
    element: (
      <ProtectedRoute requiredRoles={['LMS.Payables']}>
        <Suspense fallback={<LoadingSpinner message="Loading hauler payables..." />}>
          <HaulerPayableController />
        </Suspense>
      </ProtectedRoute>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/payables/producer-payable/:scheduleId/:poolCode/:livestockTypeId/:title',
    element: (
      <ProtectedRoute requiredRoles={['LMS.Payables']}>
        <Suspense fallback={<LoadingSpinner message="Loading producer payables..." />}>
          <ProducerPayableController />
        </Suspense>
      </ProtectedRoute>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/payables/beef-producer-payable-detail/:producerPayableId/:scheduleId/:poolCode/:livestockTypeId/:title',
    element: (
      <ProtectedRoute requiredRoles={['LMS.Payables']}>
        <Suspense fallback={<LoadingSpinner message="Loading payable details..." />}>
          <BeefProducerPayableDetail />
        </Suspense>
      </ProtectedRoute>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/payables/pork-producer-payable-detail/:producerPayableId/:scheduleId/:poolCode/:livestockTypeId/:title',
    element: (
      <ProtectedRoute requiredRoles={['LMS.Payables']}>
        <Suspense fallback={<LoadingSpinner message="Loading payable details..." />}>
          <PorkProducerPayableDetail />
        </Suspense>
      </ProtectedRoute>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/payables/producer-payable-history',
    element: (
      <ProtectedRoute requiredRoles={['LMS.Payables']}>
        <Suspense fallback={<LoadingSpinner message="Loading payable history..." />}>
          <ProducerPayableHistory />
        </Suspense>
      </ProtectedRoute>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/payables/producer-payable-history/:poolCode/:livestockTypeId',
    element: (
      <ProtectedRoute requiredRoles={['LMS.Payables']}>
        <Suspense fallback={<LoadingSpinner message="Loading payable history..." />}>
          <ProducerPayableHistory />
        </Suspense>
      </ProtectedRoute>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/payables/hauler-payable-history/:poolCode/:livestockTypeId',
    element: (
      <ProtectedRoute requiredRoles={['LMS.Payables']}>
        <Suspense fallback={<LoadingSpinner message="Loading hauler history..." />}>
          <HaulerPayableHistory />
        </Suspense>
      </ProtectedRoute>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/payables/post-adjustments/:poolCode/:livestockTypeId',
    element: (
      <ProtectedRoute requiredRoles={['LMS.Payables']}>
        <Suspense fallback={<LoadingSpinner message="Loading post adjustments..." />}>
          <PostAdjustmentList />
        </Suspense>
      </ProtectedRoute>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/payables/council-payables/:poolCode/:livestockTypeId',
    element: (
      <ProtectedRoute requiredRoles={['LMS.Payables']}>
        <Suspense fallback={<LoadingSpinner message="Loading council payables..." />}>
          <CouncilPayables />
        </Suspense>
      </ProtectedRoute>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/payables/livestock-center-payables/:poolCode/:livestockTypeId',
    element: (
      <ProtectedRoute requiredRoles={['LMS.Payables']}>
        <Suspense fallback={<LoadingSpinner message="Loading livestock center payables..." />}>
          <LivestockCenterPayables />
        </Suspense>
      </ProtectedRoute>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/payables/schedule/:poolCode/:livestockTypeId',
    element: (
      <ProtectedRoute requiredRoles={['LMS.Payables']}>
        <Suspense fallback={<LoadingSpinner message="Loading schedule payables..." />}>
          <SchedulePayableSummaries />
        </Suspense>
      </ProtectedRoute>
    ),
    errorElement: <ErrorPage />,
  },
];

export default payablesRoutes;
